import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Trash2, Plus, RefreshCw, Search, Globe, Sparkles, Database } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../App';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CountryAliasesPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ seed: [], learned: [], stats: {} });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [resolveTerm, setResolveTerm] = useState('');
  const [resolveResult, setResolveResult] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [newAlias, setNewAlias] = useState({ alias: '', canonical: '' });
  const isAdmin = user?.role === 'admin';

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/country-aliases`, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleResolve = async () => {
    if (!resolveTerm.trim()) return;
    setResolving(true);
    setResolveResult(null);
    try {
      const token = localStorage.getItem('hw_token');
      const r = await fetch(`${API}/api/country-aliases/resolve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ term: resolveTerm, context: 'manual_lookup' }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Errore');
        return;
      }
      const result = await r.json();
      setResolveResult(result);
      if (result.auto_learned) {
        toast.success(`Nuovo alias appreso via AI: ${result.term} → ${result.canonical}`);
        await fetchData();
      }
    } finally {
      setResolving(false);
    }
  };

  const handleAddAlias = async () => {
    if (!newAlias.alias.trim() || !newAlias.canonical.trim()) {
      toast.error('Inserisci alias e canonical');
      return;
    }
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/country-aliases`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(newAlias),
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Errore aggiunta alias');
      return;
    }
    toast.success(`Alias '${newAlias.alias}' → '${newAlias.canonical}' aggiunto`);
    setNewAlias({ alias: '', canonical: '' });
    await fetchData();
  };

  const handleDelete = async (alias) => {
    if (!window.confirm(`Eliminare l'alias '${alias}'?`)) return;
    const token = localStorage.getItem('hw_token');
    const r = await fetch(`${API}/api/country-aliases/${encodeURIComponent(alias)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Errore');
      return;
    }
    toast.success(`Alias '${alias}' eliminato`);
    await fetchData();
  };

  const filteredSeed = data.seed.filter(s => !search ||
    s.alias.toLowerCase().includes(search.toLowerCase()) ||
    s.canonical.toLowerCase().includes(search.toLowerCase())
  );
  const filteredLearned = data.learned.filter(s => !search ||
    s.alias.toLowerCase().includes(search.toLowerCase()) ||
    s.canonical.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-3 mb-2">
        <Globe className="h-6 w-6 text-blue-600" />
        <h1 className="text-2xl font-semibold">Libreria Stati / Cittadinanze Questura</h1>
      </div>
      <p className="text-sm text-slate-600">
        Mappatura nomi e cittadinanze multilingua → nomi ufficiali Polizia di Stato (Alloggiati Web). Quando il sistema incontra un nome non riconosciuto, lo cerca tramite AI e lo salva qui per uso futuro.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase">
            <Database className="h-4 w-4" />Stati ufficiali Polizia
          </div>
          <div className="text-2xl font-semibold mt-1">{data.stats.stati_ps_count || 0}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase">
            <Database className="h-4 w-4" />Alias seed (libreria)
          </div>
          <div className="text-2xl font-semibold mt-1">{data.stats.seed_count || 0}</div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase">
            <Sparkles className="h-4 w-4" />Alias appresi (DB)
          </div>
          <div className="text-2xl font-semibold mt-1 text-purple-600">{data.stats.learned_count || 0}</div>
        </Card>
      </div>

      {/* Resolver tool */}
      <Card className="p-4 bg-amber-50/30 border-amber-200">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-amber-600" />
          <h3 className="font-medium">Test Risoluzione (con AI fallback)</h3>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Es: Deutschland, BOSNIACA, holländisch..."
            value={resolveTerm}
            onChange={e => setResolveTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleResolve()}
            data-testid="resolve-term"
          />
          <Button onClick={handleResolve} disabled={resolving || !resolveTerm.trim()} data-testid="resolve-btn">
            {resolving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1">Risolvi</span>
          </Button>
        </div>
        {resolveResult && (
          <div className="mt-3 p-3 bg-white rounded border">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-slate-500 text-sm">'{resolveResult.term}'</span>
              <span className="text-slate-400">→</span>
              <span className="font-medium">{resolveResult.canonical || '❌ Non risolvibile'}</span>
              {resolveResult.code_questura && (
                <Badge variant="outline" className="font-mono">{resolveResult.code_questura}</Badge>
              )}
              <Badge className={
                resolveResult.source === 'direct' ? 'bg-green-100 text-green-700' :
                resolveResult.source === 'alias_seed' ? 'bg-blue-100 text-blue-700' :
                resolveResult.source === 'alias_learned' ? 'bg-purple-100 text-purple-700' :
                resolveResult.source === 'llm' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }>
                {resolveResult.source === 'direct' ? 'Match diretto' :
                 resolveResult.source === 'alias_seed' ? 'Alias libreria' :
                 resolveResult.source === 'alias_learned' ? 'Alias appreso' :
                 resolveResult.source === 'llm' ? '🤖 AI (auto-learn)' :
                 'Non trovato'}
              </Badge>
              {resolveResult.auto_learned && (
                <Badge className="bg-purple-100 text-purple-700">✓ Salvato in DB</Badge>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Add manual */}
      <Card className="p-4 bg-emerald-50/30 border-emerald-200">
        <div className="flex items-center gap-2 mb-2">
          <Plus className="h-5 w-5 text-emerald-600" />
          <h3 className="font-medium">Aggiungi alias manuale</h3>
        </div>
        <div className="flex gap-2">
          <Input placeholder="Termine alias (es. 'Suomi')" value={newAlias.alias} onChange={e => setNewAlias(p => ({ ...p, alias: e.target.value }))} data-testid="new-alias-term" />
          <Input placeholder="Nome canonico (es. 'FINLANDIA')" value={newAlias.canonical} onChange={e => setNewAlias(p => ({ ...p, canonical: e.target.value.toUpperCase() }))} data-testid="new-alias-canonical" />
          <Button onClick={handleAddAlias} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-alias-btn">
            <Plus className="h-4 w-4 mr-1" /> Aggiungi
          </Button>
        </div>
      </Card>

      {/* Lists */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input className="pl-10" placeholder="Filtra alias o canonical..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <Tabs defaultValue="learned">
        <TabsList>
          <TabsTrigger value="learned" data-testid="tab-learned">Appresi ({filteredLearned.length})</TabsTrigger>
          <TabsTrigger value="seed" data-testid="tab-seed">Libreria seed ({filteredSeed.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="learned">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Alias</th>
                  <th className="text-left px-3 py-2">Canonical</th>
                  <th className="text-left px-3 py-2">Sorgente</th>
                  <th className="text-left px-3 py-2">Quando / Chi</th>
                  {isAdmin && <th className="text-right px-3 py-2">Azioni</th>}
                </tr>
              </thead>
              <tbody>
                {filteredLearned.map(l => (
                  <tr key={l.alias} className="border-t hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">{l.alias_original || l.alias}</td>
                    <td className="px-3 py-2 font-medium">{l.canonical}</td>
                    <td className="px-3 py-2">
                      <Badge className={l.source === 'llm_auto' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                        {l.source === 'llm_auto' ? '🤖 AI' : '👤 Manuale'}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {l.learned_at?.substring(0, 16)} · {l.learned_by}
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(l.alias)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {filteredLearned.length === 0 && (
                  <tr><td colSpan={isAdmin ? 5 : 4} className="text-center py-6 text-slate-400">Nessun alias appreso ancora</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="seed">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-3 py-2">Alias</th>
                  <th className="text-left px-3 py-2">Canonical</th>
                </tr>
              </thead>
              <tbody>
                {filteredSeed.slice(0, 300).map(s => (
                  <tr key={s.alias} className="border-t">
                    <td className="px-3 py-2 font-mono text-xs">{s.alias}</td>
                    <td className="px-3 py-2 font-medium">{s.canonical}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredSeed.length > 300 && (
              <div className="text-center py-2 text-xs text-slate-400">Mostrati primi 300. Affina la ricerca.</div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {loading && <div className="text-center py-4"><RefreshCw className="h-5 w-5 inline animate-spin" /></div>}
    </div>
  );
}
